import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiFetch, ApiError } from '@avagency/frontend/lib/api';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@avagency/frontend/components/ui/card';
import { Button } from '@avagency/frontend/components/ui/button';
import { Input } from '@avagency/frontend/components/ui/input';
import { Label } from '@avagency/frontend/components/ui/label';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@avagency/frontend/components/ui/alert';

interface UpgradeResponse {
  detail?: string;
}

interface UpgradeResult {
  status: number;
  message: string;
}

async function requestUpgrade(email: string, source: string): Promise<UpgradeResult> {
  try {
    const body = await apiFetch<UpgradeResponse>('/api/v1/billing/upgrade', {
      method: 'POST',
      json: { email, source },
    });
    return { status: 200, message: body.detail ?? 'Upgrade started.' };
  } catch (err) {
    if (err instanceof ApiError) {
      // 503 is the H-021 stub path — surfacing the waitlist message
      // is a UX win (user knows they're on the list).
      const body = err.body as UpgradeResponse | null;
      return {
        status: err.status,
        message: body?.detail ?? err.message,
      };
    }
    throw err;
  }
}

type AlertVariant = 'default' | 'destructive';

function alertVariant(status: number): AlertVariant {
  if (status === 200 || status === 503) return 'default';
  return 'destructive';
}

function alertTitle(status: number): string {
  if (status === 200) return 'Upgrade started';
  if (status === 503) return 'Added to the waitlist';
  return 'Something went wrong';
}

export function UpgradeCard() {
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<UpgradeResult | null>(null);

  const mut = useMutation({
    mutationFn: (e: string) => requestUpgrade(e, 'dashboard_banner'),
    onSuccess: (res) => setResult(res),
    onError: (err) => setResult({ status: 0, message: (err as Error).message }),
  });

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email) return;
    mut.mutate(email);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upgrade to Standard — $39/mo</CardTitle>
        <CardDescription>
          Unlocks per-decline-code retry tuning, customer-facing dunning emails,
          Customer Update Portal, and the Stripe Dashboard embed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={onSubmit} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="upgrade-email">Billing email</Label>
            <div className="flex gap-2">
              <Input
                id="upgrade-email"
                type="email"
                required
                placeholder="billing@your-company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1"
              />
              <Button type="submit" disabled={mut.isPending}>
                {mut.isPending ? 'Submitting…' : 'Upgrade'}
              </Button>
            </div>
          </div>
        </form>
        {result && (
          <Alert variant={alertVariant(result.status)}>
            <AlertTitle>{alertTitle(result.status)}</AlertTitle>
            <AlertDescription>{result.message}</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
